import { AudioLines, FileWarning, Film, Grid2X2, Image, Import, List, MoreHorizontal, Search, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { formatTimecode } from '../../core/time';
import type { Asset } from '../../core/schema';
import { mediaRegistry } from '../../services/mediaRegistry';
import { useEditorStore } from '../../store/editorStore';
import { EmptyState } from '../ui/EmptyState';

function AssetIcon({ kind }: { kind: Asset['kind'] }) {
  if (kind === 'video') return <Film size={18} />;
  if (kind === 'audio') return <AudioLines size={18} />;
  if (kind === 'image') return <Image size={18} />;
  return <FileWarning size={18} />;
}

function AssetThumbnail({ asset }: { asset: Asset }) {
  const projectId = useEditorStore((state) => state.project.id);
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => { let active = true; void mediaRegistry.thumbnailUrl(projectId, asset.id).then((value) => active && setUrl(value)); return () => { active = false; }; }, [asset.id, projectId]);
  return <div className={`asset-thumbnail asset-${asset.kind}`}>{url ? <img src={url} alt="" /> : <AssetIcon kind={asset.kind} />}{asset.missing && <span className="missing-badge">!</span>}</div>;
}

export function MediaPanel() {
  const input = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [grid, setGrid] = useState(true);
  const project = useEditorStore((state) => state.project);
  const selected = useEditorStore((state) => state.selectedAssetId);
  const selectAsset = useEditorStore((state) => state.selectAsset);
  const importFiles = useEditorStore((state) => state.importFiles);
  const addToTimeline = useEditorStore((state) => state.addAssetToTimeline);
  const assets = Object.values(project.assets).filter((asset) => asset.name.toLowerCase().includes(query.toLowerCase()));

  return <div className="panel-content media-panel">
    <div className="panel-heading"><div><span className="eyebrow">PROJECT</span><h2>Media</h2></div><button className="small-primary" onClick={() => input.current?.click()}><Import size={14} /> Import</button></div>
    <input ref={input} type="file" hidden multiple accept="video/*,audio/*,image/*,.glb,.gltf,.obj,.srt,.vtt" onChange={(event) => { void importFiles([...event.target.files ?? []], false); event.target.value = ''; }} />
    <div className="media-toolbar">
      <label className="search-field"><Search size={13} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search media" /></label>
      <button onClick={() => setGrid(!grid)} title="Toggle view">{grid ? <List size={15} /> : <Grid2X2 size={15} />}</button>
    </div>
    <div className="bin-row"><span>Project media</span><span>{assets.length} items</span></div>
    {!assets.length ? <EmptyState icon={<Upload size={25} />} title="Bring in your footage" detail="Drop video, audio, or images here. Files stay on this device." action={<button className="outline-button" onClick={() => input.current?.click()}>Choose files</button>} /> :
      <div className={grid ? 'asset-grid' : 'asset-list'}>{assets.map((asset) => (
        <button key={asset.id} data-asset-id={asset.id} className={`asset-item ${selected === asset.id ? 'is-selected' : ''}`} draggable onDragStart={(event) => event.dataTransfer.setData('application/x-open-motion-asset', asset.id)} onClick={() => selectAsset(asset.id)} onDoubleClick={() => addToTimeline(asset.id)}>
          <AssetThumbnail asset={asset} />
          <div className="asset-meta"><b>{asset.name}</b><span>{asset.width && asset.height ? `${asset.width}×${asset.height}` : asset.kind.toUpperCase()}{asset.duration ? `  ·  ${formatTimecode(asset.duration, project.compositions[project.activeCompositionId].fps, false)}` : ''}</span></div>
          <MoreHorizontal size={14} className="asset-more" />
        </button>
      ))}</div>}
    <div className="panel-tip"><b>Tip</b><span>Double-click media to place it at the playhead, or drag it onto the timeline.</span></div>
  </div>;
}
